/**
 * Pure wire → UI mappers for scheduled catalog operations. Every nullable the
 * backend can send is resolved here, so components never branch on wire shapes.
 *
 * Two rules this file enforces on the way through:
 *  - `successRate: null` stays null (no runs in the window) — coercing it to 0
 *    would render a healthy new schedule as 0%.
 *  - `nextRunAt: null` stays null (Temporal unreachable) — distinct from "no
 *    upcoming runs", which the UI must say differently.
 */

import type {
  RecentRunWire,
  ScheduleDetailWire,
  ScheduleLiveWire,
  ScheduleRunWire,
  ScheduleRunsPageWire,
  SchedulePreviewResponseWire,
  SchedulesOverviewWire,
  SchedulesPageWire,
  SchedulesRibbonWire,
  ScheduleSummaryWire,
} from '../types/catalog-ops-schedule.api.types';
import type {
  ScheduleDetail,
  ScheduleLive,
  ScheduleRecentRun,
  ScheduleRun,
  ScheduleRunsPage,
  SchedulePreview,
  ScheduleSummary,
  SchedulesOverview,
  SchedulesPage,
  SchedulesRibbon,
} from '../types/catalog-ops-schedule.types';
import { specToCadenceDraft } from '../utils/schedule-cadence';

/** Server labels are optional AND can arrive blank, so a nullish check isn't
 *  enough — an empty name would render as an empty cell. */
function label(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim();
  // Length-checked rather than `??`: a blank string is not nullish, but it is
  // just as useless in a table cell.
  return trimmed !== undefined && trimmed.length > 0 ? trimmed : fallback;
}

/** Milliseconds between two optional timestamps — the API sends no duration. */
function durationMs(startedAt: string | null, completedAt: string | null): number | null {
  if (!startedAt || !completedAt) return null;
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

function toRecentRun(wire: RecentRunWire): ScheduleRecentRun {
  return {
    status: wire.status,
    startedAt: wire.startedAt,
    completedAt: wire.completedAt,
    productsSucceeded: wire.productsSucceeded,
    durationMs: durationMs(wire.startedAt, wire.completedAt),
  };
}

/**
 * Health as the list endpoint's own `health` filter defines it: derived from the
 * last NON-SKIPPED run. A schedule with no such run is neither healthy nor
 * failing — it returns `null`, which is why it matches no health filter.
 */
function deriveHealth(runs: ScheduleRecentRun[]): ScheduleSummary['health'] {
  const lastReal = [...runs].reverse().find((r) => r.status !== 'SKIPPED');
  if (!lastReal) return null;
  return lastReal.status === 'FAILED' || lastReal.status === 'CANCELLED' ? 'FAILING' : 'HEALTHY';
}

/** True while the newest run hasn't finished — what disables "Run now" without
 *  an extra fetch, since the trigger call 409s on an in-flight run. */
function deriveRunInFlight(runs: ScheduleRecentRun[]): boolean {
  const newest = runs.at(-1);
  return newest?.status === 'RUNNING' || newest?.status === 'TRIGGERED';
}

export function toScheduleSummary(wire: ScheduleSummaryWire): ScheduleSummary {
  const recentRuns = (wire.recentRuns ?? []).map(toRecentRun);
  return {
    id: wire.id,
    name: label(wire.name, 'Untitled schedule'),
    connector: wire.connector,
    useCase: wire.useCase,
    status: wire.status,
    sourceJobId: wire.sourceJobId,
    cadenceLabel: wire.cadenceLabel,
    nextRunAt: wire.nextRunAt,
    recentRuns,
    successRate: wire.successRate,
    runCount: wire.runCount ?? 0,
    productsEnriched: wire.productsEnriched ?? 0,
    health: deriveHealth(recentRuns),
    hasRunInFlight: deriveRunInFlight(recentRuns),
  };
}

export function toSchedulesPage(wire: SchedulesPageWire): SchedulesPage {
  return {
    items: (wire.items ?? []).map(toScheduleSummary),
    nextCursor: wire.nextCursor,
  };
}

export function toSchedulesRibbon(wire: SchedulesRibbonWire): SchedulesRibbon {
  return {
    total: wire.total ?? 0,
    active: wire.active ?? 0,
    paused: wire.paused ?? 0,
    runCount: wire.runCount ?? 0,
    successRate: wire.successRate,
    productsEnriched: wire.productsEnriched ?? 0,
  };
}

function toScheduleLive(wire: ScheduleLiveWire): ScheduleLive {
  return {
    paused: wire.paused,
    note: wire.note,
    nextActionTimes: wire.nextActionTimes ?? [],
    runningActions: wire.runningActions ?? [],
    recentActions: wire.recentActions ?? [],
    numActions: wire.numActions ?? 0,
    numActionsSkippedOverlap: wire.numActionsSkippedOverlap ?? 0,
    numActionsMissedCatchupWindow: wire.numActionsMissedCatchupWindow ?? 0,
    source: wire.source,
  };
}

export function toScheduleDetail(wire: ScheduleDetailWire): ScheduleDetail {
  return {
    id: wire.id,
    name: label(wire.name, 'Untitled schedule'),
    targetWorkflowType: wire.targetWorkflowType,
    sourceJobId: wire.sourceJobId,
    connector: wire.connectorCode,
    useCase: wire.useCase,
    status: wire.status,
    // Round-tripped into the cadence form so editing opens on the current spec.
    cadence: specToCadenceDraft(wire.spec),
    overlapPolicy: wire.overlapPolicy,
    catchupWindowSeconds: wire.catchupWindowSeconds,
    requestConfig: wire.requestConfig,
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt,
    createdBy: wire.createdBy,
    updatedBy: wire.updatedBy,
    live: wire.live ? toScheduleLive(wire.live) : null,
  };
}

export function toScheduleRun(wire: ScheduleRunWire): ScheduleRun {
  return {
    id: wire.id,
    workflowId: wire.workflowId,
    // The tracking layer is authoritative once its execution row exists; until
    // then the schedule's own ledger record is all there is.
    status: wire.status ?? wire.ledgerStatus,
    isLedgerStatus: wire.status === null,
    triggerType: wire.triggerType,
    startedAt: wire.startedAt,
    completedAt: wire.completedAt,
    durationMs: durationMs(wire.startedAt, wire.completedAt),
    productsSucceeded: wire.productsSucceeded,
    failureReason: wire.failureReason,
    scheduleId: wire.scheduleId,
    scheduleName: wire.scheduleName,
  };
}

export function toScheduleRunsPage(wire: ScheduleRunsPageWire): ScheduleRunsPage {
  return {
    items: (wire.items ?? []).map(toScheduleRun),
    nextCursor: wire.nextCursor,
  };
}

export function toSchedulePreview(wire: SchedulePreviewResponseWire): SchedulePreview {
  return {
    jobId: wire.jobId,
    jobName: label(wire.jobName, 'Untitled batch'),
    connector: wire.connector,
    useCase: wire.useCase,
    requestConfig: wire.requestConfig,
    configFingerprint: wire.configFingerprint,
    duplicate: wire.duplicate,
    nextRunPreview: wire.nextRunPreview ?? [],
  };
}

export function toSchedulesOverview(wire: SchedulesOverviewWire): SchedulesOverview {
  return {
    total: wire.total ?? 0,
    active: wire.active ?? 0,
    paused: wire.paused ?? 0,
    runningNow: wire.runningNow ?? 0,
    window: wire.window
      ? {
          window: wire.window.window,
          totalRuns: wire.window.totalRuns ?? 0,
          completed: wire.window.completed ?? 0,
          failed: wire.window.failed ?? 0,
          skipped: wire.window.skipped ?? 0,
          successRate: wire.window.successRate,
          productsEnriched: wire.window.productsEnriched ?? 0,
        }
      : null,
    upcoming: (wire.upcoming ?? []).map((u) => ({
      scheduleId: u.scheduleId,
      name: label(u.name, 'Untitled schedule'),
      connector: u.connector,
      nextRunAt: u.nextRunAt,
    })),
    needsAttention: (wire.needsAttention ?? []).map((a) => ({
      scheduleId: a.scheduleId,
      name: label(a.name, 'Untitled schedule'),
      reason: a.reason,
    })),
  };
}
